import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { CUSTOMER_ORDER_REPORTS_URL, REPORT_EXPORT_URL, ZONES_ENDPOINT, SUBZONES_ENDPOINT } from '../../../config/urls';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from "../../../hooks/AuthProvider";
import { useNavigate } from 'react-router-dom';
import ButtonOne from '../../../common/normal-button';
import ButtonTwo from '../../../common/dashboard-button';
import AppTable from '../../../common/app-table';
import AppDatePicker from '../../../common/app-date-picker';
import { dateColumn } from '../dateColumn';
import AppDropdown from '../../../common/app-dropdown';
import CustomerSearchInput from "./SearchInput";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

const columns = [
  { id: 'customer_name', label: 'Customer Name' },
  { id: 'customer_phone_no', label: 'Customer Phone No' },
  { id: 'zone_name', label: 'Zone' },
  { id: 'subzone_name', label: 'Sub Zone' },
  { id: 'completed_orders_count', label: 'Count' },
];

function OrdersTable({ refreshKey, searchInput, updateSearchValue }) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const navigate = useNavigate();

  const [rowCount, setRowCount] = useState(0);
  const [ordersData, setOrdersData] = useState([]);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedSubzone, setSelectedSubzone] = useState(null);
  const [zones, setZones] = useState([]);
  const [subzones, setSubzones] = useState([]);
  // Date/zone filters apply on Search/Reset; the search box applies as soon as an option is picked.
  const [appliedFilters, setAppliedFilters] = useState({});
  const [searchBoxKey, setSearchBoxKey] = useState(0);

  const getHeaders = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem('tenant');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-Tenant-Id': tenant };
  };

  // Same filters for the table and the export.
  const filterParams = () => {
    const f = appliedFilters;
    let params = '';
    if (searchInput) params += `&search_id=${encodeURIComponent(searchInput)}`;
    if (f.startDate) params += `&start_date=${dayjs(f.startDate).format('YYYY-MM-DD')}`;
    if (f.endDate) params += `&end_date=${dayjs(f.endDate).format('YYYY-MM-DD')}`;
    if (f.zone) params += `&zone=${f.zone}`;
    if (f.subzone) params += `&subzone=${f.subzone}`;
    return params;
  };

  const fetchOrders = async (isCancelled) => {
    const offset = controller.page * controller.rowsPerPage;
    const searchURL = filterParams();
    try {
      const response = await fetch(
        `${CUSTOMER_ORDER_REPORTS_URL}?limit=${controller.rowsPerPage}&offset=${offset}${searchURL}`,
        { headers: await getHeaders() }
      );
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.code === 'user_inactive') { auth.logOut(); navigate('/login'); }
        throw new Error('Failed to fetch data');
      }
      const jsonData = await response.json();
      if (isCancelled()) return;
      setOrdersData(jsonData.results);
      setRowCount(jsonData.count);
    } catch (error) {
      console.error('Error fetching data:', error.message);
    }
  };

  // Zone/Subzone dropdowns only list zones and subzones that have customers.
  const fetchZoneOptions = async () => {
    try {
      const headers = await getHeaders();
      const [zonesRes, subzonesRes] = await Promise.all([
        fetch(`${ZONES_ENDPOINT}?limit=1000&has_customers=true`, { headers }),
        fetch(`${SUBZONES_ENDPOINT}?limit=1000&has_customers=true`, { headers }),
      ]);
      if (zonesRes.ok) setZones((await zonesRes.json()).results || []);
      if (subzonesRes.ok) setSubzones((await subzonesRes.json()).results || []);
    } catch (error) { console.error('Error fetching zones:', error); }
  };

  useEffect(() => { fetchZoneOptions(); }, []);

  // Subzone dropdown follows the selected zone.
  const visibleSubzones = selectedZone ? subzones.filter((s) => s.zone?.id === selectedZone) : subzones;

  // Single fetch path: every page change and filter change refetches with all filters,
  // and responses from superseded requests are dropped.
  useEffect(() => {
    let cancelled = false;
    fetchOrders(() => cancelled);
    return () => { cancelled = true; };
  }, [controller, appliedFilters, searchInput, refreshKey]);

  useEffect(() => {
    setController((p) => (p.page ? { ...p, page: 0 } : p));
  }, [searchInput]);

  const applyFilters = (filters) => {
    setAppliedFilters(filters);
    setController((p) => ({ ...p, page: 0 }));
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedZone(null);
    setSelectedSubzone(null);
    updateSearchValue('');
    setSearchBoxKey((k) => k + 1);
    applyFilters({});
  };

  const handleExport = async () => {
    const tenant = localStorage.getItem('tenant');
    const token = await getValidToken();
    try {
      const response = await fetch(`${REPORT_EXPORT_URL}?model=customer${filterParams()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'X-Tenant-Id': tenant },
      });
      if (!response.ok) throw new Error('Failed to export data');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      a.download = contentDisposition ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || 'report.csv' : 'report.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error.message);
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: "none" }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', py: 1, my: 1, bgcolor: 'background.paper', borderRadius: 1, alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <CustomerSearchInput key={searchBoxKey} updateSearchValue={updateSearchValue} />
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ flex: { xs: 1, sm: '0 0 258px' }, minWidth: 0 }}>
              <AppDatePicker
                value={startDate}
                onChange={(date) => setStartDate(date)}
                placeholder="Start Date"
              />
            </Box>
            <Box sx={{ flex: { xs: 1, sm: '0 0 258px' }, minWidth: 0 }}>
              <AppDatePicker
                value={endDate}
                onChange={(date) => setEndDate(date)}
                placeholder="End Date"
                minDate={startDate || undefined}
              />
            </Box>
          </Box>

          <Tooltip title="Filter by Zone" placement="top" arrow>
            <span>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={zones.map((z) => ({ label: z.name, value: z.id }))}
                searchable
                searchPlaceholder="Search zone"
                selectedValue={selectedZone}
                onMenuSelect={(value) => { setSelectedZone(value); setSelectedSubzone(null); }}
                sx={{ minWidth: 120 }}
              >
                {selectedZone ? zones.find((z) => z.id === selectedZone)?.name : 'Zone'}
              </AppDropdown>
            </span>
          </Tooltip>

          <Tooltip title="Filter by Subzone" placement="top" arrow>
            <span>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={visibleSubzones.map((s) => ({ label: s.name, value: s.id }))}
                searchable
                searchPlaceholder="Search subzone"
                selectedValue={selectedSubzone}
                onMenuSelect={(value) => {
                  setSelectedSubzone(value);
                  // Picking a subzone first fills in its zone.
                  const zoneId = subzones.find((s) => s.id === value)?.zone?.id;
                  if (zoneId) setSelectedZone(zoneId);
                }}
                sx={{ minWidth: 120 }}
              >
                {selectedSubzone ? subzones.find((s) => s.id === selectedSubzone)?.name : 'Subzone'}
              </AppDropdown>
            </span>
          </Tooltip>

          <ButtonOne variant="contained" size="large" onClick={() => applyFilters({ startDate, endDate, zone: selectedZone, subzone: selectedSubzone })}>
            Search
          </ButtonOne>
          <ButtonTwo variant="outlined" size="large" onClick={handleReset}>
            Reset
          </ButtonTwo>
          <ButtonOne variant="contained" size="large" onClick={handleExport}>
            Export
          </ButtonOne>
        </LocalizationProvider>
      </Box>

      <AppTable
        columns={[...columns, dateColumn]}
        rows={ordersData}
        rowKey={(o, i) => i}
        emptyMessage="No data found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(e) => setController((prev) => ({ ...prev, rowsPerPage: parseInt(e.target.value, 10), page: 0 }))}
      />
    </Paper>
  );
}

export default OrdersTable;